<script setup lang="ts">
/*
* Компонент UserVisibilitySettings - настройки видимости личных данных сотрудника
*
* Основные функции:
* - Управление флагами сокрытия личных данных (телефон, рабочий телефон, email, дата рождения, год рождения)
* - Сохранение настроек через PATCH /api/account/iam/
* - Блокировка кнопки сохранения при отсутствии изменений
*/

import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/modules/user/stores/userStore'

const userStore = useUserStore()

// Локальные реактивные состояния для свитчей видимости (true = данные видны сотрудникам, false = скрыты)
const showPersonalPhone = ref(false)
const showWorkPhone = ref(false)
const showEmail = ref(false)
const showBirthDate = ref(false)
const showBirthYear = ref(false)

const fieldMap = {
  is_hide_phone_number: showPersonalPhone,
  is_hide_work_phone_number: showWorkPhone,
  is_hide_email: showEmail,
  is_hide_birth_date: showBirthDate,
  is_hide_birth_year: showBirthYear,
} as const

const resetForm = () => {
  if (!userStore.user) return
  for (const [key, state] of Object.entries(fieldMap)) {
    state.value = userStore.user[key as keyof typeof fieldMap] === false
  }
}

watch(() => userStore.user, () => {
  resetForm()
}, { immediate: true })

const hasChanges = computed(() => {
  if (!userStore.user) return false
  return Object.entries(fieldMap).some(
    ([key, state]) => state.value !== (userStore.user![key as keyof typeof fieldMap] === false),
  )
})

const saveSettings = async () => {
  if (!hasChanges.value || !userStore.user) return

  const payload: Record<string, boolean> = {}
  for (const [key, state] of Object.entries(fieldMap)) {
    if (state.value !== (userStore.user[key as keyof typeof fieldMap] === false)) {
      payload[key] = !state.value
    }
  }

  await userStore.patchUserProfile(payload)
}
</script>

<template>
  <VCard
    class="pa-6 elevation-6 rounded"
    max-width="560"
  >
    <div class="text-body-1 text-medium-emphasis mb-6">
      Настройте, какие личные данные будут видны другим сотрудникам
    </div>

    <div class="d-flex flex-column ga-5">
      <div class="d-flex align-center justify-space-between">
        <span class="text-body-1 text-high-emphasis">Личный телефон</span>
        <VSwitch
          v-model="showPersonalPhone"
          color="primary"
          inset
          hide-details
          density="compact"
        />
      </div>

      <div class="d-flex align-center justify-space-between">
        <span class="text-body-1 text-high-emphasis">Рабочий телефон</span>
        <VSwitch
          v-model="showWorkPhone"
          color="primary"
          inset
          hide-details
          density="compact"
        />
      </div>

      <div class="d-flex align-center justify-space-between">
        <span class="text-body-1 text-high-emphasis">Электронная почта</span>
        <VSwitch
          v-model="showEmail"
          color="primary"
          inset
          hide-details
          density="compact"
        />
      </div>

      <div class="d-flex align-center justify-space-between">
        <span class="text-body-1 text-high-emphasis">Дата рождения</span>
        <VSwitch
          v-model="showBirthDate"
          color="primary"
          inset
          hide-details
          density="compact"
        />
      </div>

      <div class="d-flex align-center justify-space-between">
        <span
          class="text-body-1"
          :class="!showBirthDate ? 'text-disabled' : 'text-high-emphasis'"
        >
          Год рождения
        </span>
        <VSwitch
          v-model="showBirthYear"
          :disabled="!showBirthDate"
          color="primary"
          inset
          hide-details
          density="compact"
        />
      </div>
    </div>

    <div class="d-flex justify-end mt-6">
      <VBtn
        color="primary"
        :disabled="!hasChanges"
        @click="saveSettings"
      >
        Сохранить
      </VBtn>
    </div>
  </VCard>
</template>
